import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Activity } from 'lucide-react';
import api from '../../services/api';

const normSymbol = (s) => String(s || '').trim().toUpperCase();

const quotePrice = (row) => {
  if (!row || row.error) return NaN;
  const p = row.price != null && row.price !== '' ? parseFloat(row.price) : NaN;
  if (!Number.isNaN(p) && p > 0) return p;
  const pre = row.preClose != null && row.preClose !== '' ? parseFloat(row.preClose) : NaN;
  if (!Number.isNaN(pre) && pre > 0) return pre;
  return Number.isNaN(p) ? NaN : p;
};

/**
 * Hero 右侧浮动交易卡片 — 展示最新未平仓/已平仓交易，并拉实时价计算涨幅。
 */
const HeroTradeCards = () => {
  const [trades, setTrades] = useState([]);
  const [quotes, setQuotes] = useState({});

  useEffect(() => {
    let cancelled = false;
    api
      .get('/trades', { params: { limit: 8 } })
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.success ? res?.data?.data?.trades || [] : [];
        setTrades(list.slice(0, 4));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const activeSymbols = useMemo(
    () => [
      ...new Set(
        trades
          .filter((t) => t.status === 'active')
          .map((t) => normSymbol(t.symbol))
          .filter(Boolean)
      ),
    ],
    [trades]
  );

  useEffect(() => {
    if (activeSymbols.length === 0) {
      setQuotes({});
      return undefined;
    }

    let cancelled = false;

    const fetchQuotes = async () => {
      try {
        const res = await api.post('/trades/stock-prices', { symbols: activeSymbols });
        if (cancelled || !res.data?.success || !res.data?.data) return;
        const next = {};
        for (const key of Object.keys(res.data.data)) {
          const sym = normSymbol(key);
          const row = res.data.data[key];
          if (row && !row.error) next[sym] = row;
        }
        setQuotes((prev) => ({ ...prev, ...next }));
      } catch (_) {
        /* ignore */
      }
    };

    fetchQuotes();
    const timer = window.setInterval(fetchQuotes, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeSymbols.join('|')]);

  const resolvePrice = (trade) => {
    const key = normSymbol(trade.symbol);
    const live = quotePrice(quotes[key]);
    if (trade.status === 'active') {
      if (!Number.isNaN(live) && live > 0) return live;
      const db = parseFloat(trade.currentPrice);
      if (!Number.isNaN(db) && db > 0) return db;
      const entry = parseFloat(trade.entryPrice);
      if (!Number.isNaN(entry) && entry > 0) return entry;
      return 0;
    }
    return parseFloat(trade.exitPrice) || parseFloat(trade.currentPrice) || parseFloat(trade.entryPrice) || 0;
  };

  const formatChange = (trade) => {
    const entry = parseFloat(trade.entryPrice);
    const current = resolvePrice(trade);
    if (entry > 0 && current > 0) {
      return ((current - entry) / entry) * 100;
    }
    const ratio = parseFloat(trade.pnlRatio);
    return Number.isFinite(ratio) ? ratio : 0;
  };

  const skeletons = [0, 1, 2, 3];
  const items = trades.length ? trades : skeletons.map((i) => ({ __skeleton: true, id: `sk-${i}` }));

  return (
    <div className="hero-cards" aria-hidden={trades.length === 0 ? 'true' : 'false'}>
      {items.map((t, idx) => {
        if (t.__skeleton) {
          return (
            <div key={t.id} className={`hero-card hero-card--skeleton hero-card--slot-${idx}`} />
          );
        }
        const ratio = formatChange(t);
        const up = ratio >= 0;
        const TrendIcon = up ? TrendingUp : TrendingDown;
        const price = resolvePrice(t);
        return (
          <div
            key={t.id}
            className={`hero-card hero-card--slot-${idx} ${up ? 'hero-card--up' : 'hero-card--down'}`}
          >
            <div className="hero-card__top">
              <div className="hero-card__sym">
                <span className="hero-card__sym-code">{t.symbol}</span>
                <span className="hero-card__sym-market">{t.market || 'NASDAQ'}</span>
              </div>
              <div className={`hero-card__badge ${up ? 'hero-card__badge--up' : 'hero-card__badge--down'}`}>
                <TrendIcon size={12} strokeWidth={2.5} />
                <span>{up ? '+' : ''}{ratio.toFixed(2)}%</span>
              </div>
            </div>
            <div className="hero-card__price">${price.toFixed(2)}</div>
            <div className="hero-card__foot">
              <span className="hero-card__live">
                <Activity size={10} />
                {t.status === 'active' ? 'LIVE' : 'CLOSED'}
              </span>
              <span className="hero-card__qty">Qty · {t.quantity?.toLocaleString() || 0}</span>
            </div>
            <svg className="hero-card__spark" viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true">
              <polyline
                points={
                  up
                    ? '0,24 15,20 30,22 45,16 60,18 75,10 90,12 100,6'
                    : '0,8 15,10 30,7 45,14 60,12 75,20 90,18 100,24'
                }
                fill="none"
                strokeWidth="2"
                stroke={up ? '#22c55e' : '#f43f5e'}
                opacity="0.85"
              />
            </svg>
          </div>
        );
      })}
    </div>
  );
};

export default HeroTradeCards;
